<template>
  <el-form
    ref="form"
    size="medium"
    v-loading="loading"
    :model="form"
    :rules="rules"
    :validate-on-rule-change="true"
    label-width="150px"
  >
    <el-row>
      <div class="section-title">主体信息</div>
      <el-col :span="24">
        <el-form-item label="组织机构名称" prop="companyName">
          <el-input :disabled="isEdit" v-model="form.companyName" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item label="统一社会信用代码" prop="socialCreditCode">
          <el-input :disabled="isEdit" v-model="form.socialCreditCode" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item label="组织机构续存证明" prop="companyLicenseList">
          <div class="upload-width-example">
            <verify-uploader
              action="/action/upload"
              list-type="picture-card"
              :accept="`.${imageType.join(',.')}`"
              :limit="1"
              :class="{'has-logo': form.companyLicenseList.length}"
              :disabled="fileUploading || isEdit"
              :file-list="form.companyLicenseList"
              :before-upload="handleLicenseBefore"
              :on-preview="handleLicensePreview"
              :on-success="handleLicenseSuccess"
              :on-remove="handleLicenseRemove"
              :on-error="handleLicenseError"
            >
              <i class="el-icon-plus" />
              <div slot="tip" class="el-upload__tip">
                <div>· 请上传统一社会信用代码清晰可见的相关资质照片</div>
                <div>· 复印件需手写“与原件一致”，并于文字处加盖公司鲜章</div>
                <div>· 支持jpg、png、jpeg格式图片，且大小不能大于10MB</div>
                <div>· 仅可上传一张，如需替换请先删除</div>
              </div>
            </verify-uploader>
            <div class="example" @click="handleLicensePreview({url:licenseExampleUrl})" v-if="!isEdit">
              <img
                class="example-img"
                src="@/assets/images/license_example.png"
              >
              <div class="tip">示例图<i class="el-icon-zoom-in" /></div>
            </div>
          </div>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.legalPerson')" prop="corporation">
          <el-input :disabled="isEdit" v-model="form.corporation" :placeholder="$t('person.legalPersonPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>

      <div class="section-title">联系人信息</div>
      <el-col :span="24">
        <el-form-item label="联系人姓名" prop="name">
          <el-input
            v-model="form.name"
            placeholder="请填写联系人姓名"
            class="form-field"
            type="text"
            autocomplete
            maxlength="10"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.phone')" prop="phoneNum">
          <el-input v-model="form.phoneNum" :placeholder="$t('person.phonePlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.email')" prop="email">
          <el-input
            v-model="form.email"
            :placeholder="$t('person.emailPlaceholder')"
            class="form-field"
            type="email"
            autocomplete
          ></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.address')" prop="cpAddress">
          <el-input v-model="form.cpAddress" :placeholder="$t('person.addressPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>

      <el-col :span="24">
        <el-form-item label="邮政编码" prop="postalcode">
          <el-input
            v-model="form.postalcode"
            placeholder="请填写联系人地址区域的邮政编码"
            class="form-field"
            maxlength="10"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-col>

      <el-col :span="24">
        <el-form-item :label="$t('person.qq')" prop="qqNum">
          <el-input v-model="form.qqNum" :placeholder="$t('person.qqPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import { imageType } from '@/utils/constants'
import { emailValidate, validateFileExt } from '@/utils/validate'
import VerifyUploader from '@/components/VerifyUploader'
import { Organization } from '../../const'
import licenseExampleUrl from '@/assets/images/license_example.png'

export default {
  name: 'OrganizationForm',
  components: {
    VerifyUploader
  },
  props: {
    isEdit: Boolean,
    defaultData: Object,
    loading: Boolean,
  },
  data() {
    const isZH = this.$i18n.locale === 'zh'

    const setRequired = (zhText, enText, trigger = 'blur') => {
      return {
        required: true,
        message: isZH ? zhText : enText,
        trigger
      }
    }
    const setValidator = (fn, zhText, enText, trigger = 'blur') => {
      const validator = (rule, value, cb) => {
        if (fn(value)) return cb()
        cb(new Error(isZH ? zhText : enText))
      }
      return {
        validator,
        trigger
      }
    }

    return {
      imageType,
      licenseExampleUrl,

      form: new Organization(),
      rules: {
        companyName: [setRequired('请填写组织机构名称', 'Please input your organization name')],
        socialCreditCode: [setRequired('请输入统一社会信用代码', 'Please input social credit code')],
        companyLicenseList: [{ ...setRequired('请上传证明', 'Please upload file'), type: 'array' }],
        corporation: [setRequired('请填写法人代表', 'Please input the legal person')],
        name: [setRequired('请输入联系人姓名', 'Please input name')],
        email: [
          setRequired('请填写邮箱', 'Please input your email'),
          setValidator(emailValidate, '请填写正确邮箱', 'wrong email')
        ],
        cpAddress: [setRequired('请填写联系地址', 'Please input your address')],
        phoneNum: [setRequired('请填写联系电话', 'Please input your phone number')],
      },
      fileUploading: false,
    }
  },
  created() {
    if (this.defaultData) {
      this.form = new Organization(this.defaultData)
    }
  },
  mounted() {
    this.$refs.form.clearValidate()
  },
  methods: {
    handleLicenseBefore(file) {
      if (!validateFileExt(imageType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return Promise.reject()
      }

      if (file.size > 10 * 1024 * 1024) {
        this.$message.warning('文件大于 10M')
        return Promise.reject()
      }

      this.fileUploading = true
    },
    handleLicenseSuccess(resp, file, fileList) {
      const respData = resp.data
      const fileData = {
        url: this.$toPreview(respData.url),
        md5: respData.md5,
        fileName: file.name,
        photoPath: respData.file_id,
        photoUrl: respData.url,
        photoSize: file.size,
        photoHeight: respData.file_info.height,
        photoWidth: respData.file_info.width,
        orderIndex: 0,
      }
      this.form.companyLicenseList.push(fileData)
      this.fileUploading = false
    },
    handleLicenseError(err, file, fileList) {
      this.form.companyLicenseList = fileList
      this.fileUploading = false
      this.$message({
        type: 'error',
        message: '上传证明失败: ' + err.message,
        error: err
      })
    },
    handleLicenseRemove(file, fileList) {
      this.form.companyLicenseList = fileList
    },
    handleLicensePreview(file) {
      open(file.url, '_blank')
    },

    async genData() {
      const valid = await this.$refs.form.validate().catch(_ => {
        this.$alert('有字段未填写完整, 请检查', '提示', { type: 'warning' })
      })

      if (!valid) throw new Error('表单校验失败')

      const postData = this.form.getBackendData()
      return postData
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.other-info {
  /deep/ .el-form-item {
    margin-bottom: 16px
  }
}

.form-field {
  width: 600px;
}
.el-icon-plus {
  cursor: pointer;
  color: $success;
}
.el-icon-delete {
  cursor: pointer;
  color: $danger;
  margin-left: 8px;
}
.el-upload__tip {
  div {
    line-height: 2;
  }
}

.el-icon-plus {
  font-weight: 700;
}

.upload-width-example {
  position: relative;

  .example {
    position: absolute;
    top: 0;
    left: 156px;
    background-color: #fbfdff;
    border: 1px dashed #c0ccda;
    border-radius: 6px;
    box-sizing: border-box;
    width: 148px;
    height: 148px;
    cursor: pointer;
    text-align: center;

    .example-img {
      z-index: 99;
      width: 146px;
      height: 146px;
      padding: 6px;
      padding-bottom: 38px;
      box-sizing: border-box;
    }

    .tip {
      width: 100%;
      position: absolute;
      bottom: 0;
      color: #c0ccda;
      vertical-align: middle;
    }

    .el-icon-zoom-in {
        margin-left: 6px;
        font-size: 18px;
        vertical-align: baseline;
    }
  }

  .has-logo {
    /deep/ .el-upload--picture-card {
      display: none;
    }
  }
}

</style>
